<template>
  <div class="audit-form mx-4 col-6">
    <b-form-group class="form-group_center" label="Enable audit notifications" label-cols="4">
      <b-form-checkbox
        v-model="settings.audit_enabled"
        :disabled="saving || !canControl"
        @change="saveSetting('audit_enabled')"
      />
    </b-form-group>
    <b-form-group
      v-if="settings.audit_enabled"
      label="Notification email"
      label-cols="4"
      :state="!serverErrors.audit_email"
    >
      <b-input-group>
        <b-form-input
          v-model="emailInput"
          type="email"
          placeholder="admin@example.com"
          :disabled="saving || !canControl"
          @blur="emailInputDirty && saveEmailAndSendCode()"
        />
        <b-input-group-append v-if="canSendCode">
          <b-button
            variant="outline-primary"
            :disabled="saving"
            @click="saveEmailAndSendCode"
          >
            Send code
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <template #invalid-feedback>
        <div v-if="serverErrors.audit_email">{{ getServerFieldErrors('audit_email') }}</div>
      </template>
      <small v-if="settings.audit_email_status === 'confirmed'" class="text-success">
        Email confirmed
      </small>
      <small v-else-if="settings.audit_email_status === 'waiting_confirmation'" class="text-secondary">
        Waiting confirmation — code sent at {{ formatDate(settings.audit_email_code_sent_at) }}
      </small>
      <small v-else-if="settings.audit_email && emailInputDirty" class="text-muted">
        Save email and send verification code to confirm
      </small>
    </b-form-group>

    <b-form-group
      v-if="settings.audit_enabled && settings.audit_email_status === 'waiting_confirmation'"
      label="Confirmation code"
      label-cols="4"
    >
      <b-input-group>
        <b-form-input
          v-model="confirmationCode"
          type="text"
          maxlength="6"
          placeholder="6-digit code"
          :disabled="saving || !canControl"
        />
        <b-input-group-append>
          <b-button
            variant="outline-primary"
            :disabled="saving || !canControl || confirmationCode.length !== 6"
            @click="verifyCode"
          >
            Confirm
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <small v-if="codeError" class="text-danger">{{ codeError }}</small>
    </b-form-group>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'Audit',
  data: () => ({
    saving: false,
    emailInput: '',
    emailInputDirty: false,
    confirmationCode: '',
    codeError: null,
    serverErrors: {},
  }),
  computed: {
    ...mapState('settings', ['auditSettings']),
    ...mapGetters('settings', ['canControl']),
    canSendCode() {
      return this.settings.audit_email_status !== 'confirmed'
        && this.canControl
        && this.emailInput
        && dayjs().diff(dayjs(this.settings.audit_email_code_sent_at), 'minute') > 5
    },
    settings() {
      return this.auditSettings
    },
  },
  watch: {
    'auditSettings.audit_email'(val) {
      this.emailInput = val || ''
      this.emailInputDirty = false
    },
    emailInput(val) {
      this.emailInputDirty = val !== (this.auditSettings.audit_email || '')
    },
    confirmationCode() {
      this.codeError = null
    },
  },
  created() {
    this.loadAuditSettings().then(() => {
      this.emailInput = this.auditSettings.audit_email || ''
    })
  },
  methods: {
    ...mapActions('settings', [
      'loadAuditSettings',
      'saveAuditSettings',
      'sendAuditConfirmation',
      'confirmAuditEmail',
    ]),
    getServerFieldErrors(fieldName) {
      return this.serverErrors[fieldName]?.join(', ') ?? ''
    },
    async saveSetting(field) {
      this.serverErrors = {}
      try {
        this.saving = true
        await this.saveAuditSettings({ [field]: this.settings[field] })
      } catch (e) {
        if (e.response?.status === 422) {
          this.$set(this, 'serverErrors', e.response.data.errors)
        } else {
          await this.loadAuditSettings()
        }
      } finally {
        this.saving = false
      }
    },
    async saveEmailAndSendCode() {
      this.serverErrors = {}
      try {
        this.saving = true
        if (this.emailInputDirty) {
          await this.saveAuditSettings({ audit_email: this.emailInput })
        }
        await this.sendAuditConfirmation()
        this.confirmationCode = ''
      } catch (e) {
        if (e.response?.status === 422) {
          this.$set(this, 'serverErrors', e.response.data.errors)
        } else {
          await this.loadAuditSettings()
        }
      } finally {
        this.saving = false
      }
    },
    async verifyCode() {
      try {
        this.saving = true
        this.codeError = null
        await this.confirmAuditEmail({ code: this.confirmationCode })
        this.confirmationCode = ''
      } catch (e) {
        this.codeError = e.response?.data?.errors?.code?.[0] ?? 'Invalid confirmation code.'
      } finally {
        this.saving = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString()
    },
  },
}
</script>

<style lang="scss">
.audit-form {
  max-width: 800px;
}
</style>
